import type { ReactNode } from 'react'
import { MailboxNavIcons } from './MailboxNav.icons'

export type MailboxNavMenuItem = {
  id: string
  label: string
  icon?: ReactNode
}

export type MailboxNavColorOption = {
  id: string
  /** CSS color value (hex or token). */
  color: string
  label?: string
}

export type MailboxNavItem = {
  id: string
  label: ReactNode
  /** Design-system icon node (prefer `MailboxNavIcons.*`). */
  icon?: ReactNode
  count?: number
  hasUnread?: boolean
  /** Label color (CSS color / token). Tints the icon when set. */
  color?: string
  /** Overflow actions (Rename / Delete). Shown on hover. */
  menuItems?: MailboxNavMenuItem[]
  disabled?: boolean
}

export type MailboxNavGroup = {
  id: string
  items: MailboxNavItem[]
}

export type MailboxNavSectionKind = 'folder' | 'label'

export type MailboxNavSection = {
  id: string
  title: ReactNode
  /** Design-system icon node for the section header. */
  icon?: ReactNode
  items: MailboxNavItem[]
  /**
   * `label` shows a color picker on create/edit.
   * `folder` is name-only.
   */
  kind?: MailboxNavSectionKind
  /** Color swatches for `kind: 'label'`. Defaults to `MAILBOX_NAV_LABEL_COLORS`. */
  colorOptions?: MailboxNavColorOption[]
  open?: boolean
  defaultOpen?: boolean
  onOpenChange?: (open: boolean) => void
  /** Called when + is clicked (in addition to opening the create form when `onCreate` is set). */
  onAdd?: () => void
  addAriaLabel?: string
  createPlaceholder?: string
  itemIcon?: ReactNode
}

export type MailboxNavCreatePayload = {
  sectionId: string
  name: string
  color?: string
}

export type MailboxNavUpdatePayload = {
  sectionId: string
  itemId: string
  name: string
  color?: string
}

export type MailboxNavPreviewState =
  | 'default'
  | 'selected'
  | 'hover'
  | 'collapsed'
  | 'with-overflow'
  | 'creating-label'
  | 'email-sample'

export const MAILBOX_NAV_PREVIEW_STATES: MailboxNavPreviewState[] = [
  'default',
  'selected',
  'hover',
  'collapsed',
  'with-overflow',
  'creating-label',
  'email-sample',
]

export const MAILBOX_NAV_PREVIEW_STATE_LABELS: Record<
  MailboxNavPreviewState,
  string
> = {
  default: 'Default (Fax)',
  selected: 'Selected',
  hover: 'Hover',
  collapsed: 'Sections collapsed',
  'with-overflow': 'Item overflow',
  'creating-label': 'Creating label',
  'email-sample': 'Email sample',
}

export const MAILBOX_NAV_FOLDER_MENU: MailboxNavMenuItem[] = [
  { id: 'rename', label: 'Rename', icon: MailboxNavIcons.edit() },
  { id: 'delete', label: 'Delete', icon: MailboxNavIcons.delete() },
]

export const MAILBOX_NAV_LABEL_MENU: MailboxNavMenuItem[] = [
  { id: 'edit', label: 'Edit', icon: MailboxNavIcons.edit() },
  { id: 'delete', label: 'Delete', icon: MailboxNavIcons.delete() },
]

export const MAILBOX_NAV_LABEL_COLORS: MailboxNavColorOption[] = [
  { id: 'coral', color: '#FE746B', label: 'Coral' },
  { id: 'orange', color: '#ED9A5E', label: 'Orange' },
  { id: 'yellow', color: '#F5D565', label: 'Yellow' },
  { id: 'green', color: '#9DD8AC', label: 'Green' },
  { id: 'sky', color: '#65ACF5', label: 'Sky' },
  { id: 'blue', color: '#185DD8', label: 'Blue' },
  { id: 'purple', color: '#B794F4', label: 'Purple' },
  { id: 'red', color: '#E57373', label: 'Red' },
  { id: 'slate', color: '#4A5568', label: 'Slate' },
]

/** Fax — system folders (two groups with a divider). */
export const MAILBOX_NAV_FAX_GROUPS: MailboxNavGroup[] = [
  {
    id: 'primary',
    items: [
      {
        id: 'inbox',
        label: 'Inbox',
        icon: MailboxNavIcons.inbox(),
        count: 12,
        hasUnread: true,
      },
      {
        id: 'draft',
        label: 'Drafts',
        icon: MailboxNavIcons.drafts(),
        count: 2,
      },
      {
        id: 'sent',
        label: 'Sent',
        icon: MailboxNavIcons.sent(),
        count: 48,
      },
      {
        id: 'history',
        label: 'History',
        icon: MailboxNavIcons.history(),
        count: 120,
      },
    ],
  },
  {
    id: 'secondary',
    items: [
      {
        id: 'archive',
        label: 'Archive',
        icon: MailboxNavIcons.archive(),
        count: 6,
      },
      {
        id: 'trash',
        label: 'Trash',
        icon: MailboxNavIcons.trash(),
        count: 3,
      },
      { id: 'blocked', label: 'Blocked', icon: MailboxNavIcons.blocked() },
    ],
  },
]

export const MAILBOX_NAV_FAX_SECTIONS: MailboxNavSection[] = [
  {
    id: 'folders',
    title: 'Folders',
    icon: MailboxNavIcons.folder(),
    kind: 'folder',
    defaultOpen: true,
    addAriaLabel: 'Add folder',
    createPlaceholder: 'Add folder',
    itemIcon: MailboxNavIcons.folder(),
    items: [
      {
        id: 'folder-jones',
        label: 'Dr. Jones',
        icon: MailboxNavIcons.folder(),
        count: 4,
        menuItems: MAILBOX_NAV_FOLDER_MENU,
      },
      {
        id: 'folder-claims',
        label: 'Insurance claims',
        icon: MailboxNavIcons.folder(),
        count: 9,
        hasUnread: true,
        menuItems: MAILBOX_NAV_FOLDER_MENU,
      },
      {
        id: 'folder-legal',
        label: 'Legal docs',
        icon: MailboxNavIcons.folder(),
        menuItems: MAILBOX_NAV_FOLDER_MENU,
      },
    ],
  },
  {
    id: 'labels',
    title: 'Labels',
    icon: MailboxNavIcons.tag(),
    kind: 'label',
    defaultOpen: true,
    addAriaLabel: 'Add label',
    createPlaceholder: 'Add label',
    itemIcon: MailboxNavIcons.tag(),
    items: [
      {
        id: 'label-urgent',
        label: 'Urgent',
        icon: MailboxNavIcons.tag(),
        color: '#FE746B',
        count: 5,
        menuItems: MAILBOX_NAV_LABEL_MENU,
      },
      {
        id: 'label-review',
        label: 'Needs review',
        icon: MailboxNavIcons.tag(),
        color: '#ED9A5E',
        count: 2,
        menuItems: MAILBOX_NAV_LABEL_MENU,
      },
      {
        id: 'label-patient',
        label: 'Patient',
        icon: MailboxNavIcons.tag(),
        color: '#9DD8AC',
        menuItems: MAILBOX_NAV_LABEL_MENU,
      },
    ],
  },
]

/** Email — system mailboxes. */
export const MAILBOX_NAV_EMAIL_GROUPS: MailboxNavGroup[] = [
  {
    id: 'primary',
    items: [
      {
        id: 'inbox',
        label: 'Inbox',
        icon: MailboxNavIcons.inbox(),
        count: 24,
        hasUnread: true,
      },
      {
        id: 'drafts',
        label: 'Drafts',
        icon: MailboxNavIcons.drafts(),
        count: 1,
      },
      {
        id: 'sent',
        label: 'Sent',
        icon: MailboxNavIcons.sent(),
        count: 86,
      },
      {
        id: 'starred',
        label: 'Starred',
        icon: MailboxNavIcons.starred(),
        count: 7,
      },
    ],
  },
  {
    id: 'secondary',
    items: [
      {
        id: 'all',
        label: 'All mail',
        icon: MailboxNavIcons.mail(),
        count: 312,
      },
      {
        id: 'archive',
        label: 'Archive',
        icon: MailboxNavIcons.archive(),
        count: 40,
      },
      {
        id: 'spam',
        label: 'Spam',
        icon: MailboxNavIcons.spam(),
        count: 11,
      },
      {
        id: 'trash',
        label: 'Trash',
        icon: MailboxNavIcons.trash(),
        count: 0,
      },
    ],
  },
]

export const MAILBOX_NAV_EMAIL_SECTIONS: MailboxNavSection[] = [
  {
    id: 'folders',
    title: 'Folders',
    icon: MailboxNavIcons.folder(),
    kind: 'folder',
    defaultOpen: true,
    addAriaLabel: 'Add folder',
    createPlaceholder: 'Add folder',
    itemIcon: MailboxNavIcons.folder(),
    items: [
      {
        id: 'folder-billing',
        label: 'Billing',
        icon: MailboxNavIcons.folder(),
        count: 14,
        menuItems: MAILBOX_NAV_FOLDER_MENU,
      },
      {
        id: 'folder-referrals',
        label: 'Referrals',
        icon: MailboxNavIcons.folder(),
        count: 3,
        hasUnread: true,
        menuItems: MAILBOX_NAV_FOLDER_MENU,
      },
    ],
  },
  {
    id: 'labels',
    title: 'Labels',
    icon: MailboxNavIcons.tag(),
    kind: 'label',
    defaultOpen: true,
    addAriaLabel: 'Add label',
    createPlaceholder: 'Add label',
    itemIcon: MailboxNavIcons.tag(),
    items: [
      {
        id: 'label-vip',
        label: 'VIP',
        icon: MailboxNavIcons.tag(),
        color: '#B794F4',
        count: 8,
        menuItems: MAILBOX_NAV_LABEL_MENU,
      },
      {
        id: 'label-followup',
        label: 'Follow-up',
        icon: MailboxNavIcons.tag(),
        color: '#185DD8',
        count: 6,
        menuItems: MAILBOX_NAV_LABEL_MENU,
      },
    ],
  },
]

export type MailboxNavProps = {
  /** System mailbox groups; a divider is rendered between groups. */
  groups?: MailboxNavGroup[]
  /** Collapsible Folders / Labels sections. */
  sections?: MailboxNavSection[]
  value?: string
  defaultValue?: string
  onChange?: (id: string) => void
  /**
   * Called for overflow actions that are not handled as built-in
   * rename/edit/delete (or when create/update/delete handlers are omitted).
   */
  onItemMenuSelect?: (itemId: string, menuId: string) => void
  /** Save handler for the inline create form (+ button). */
  onCreate?: (payload: MailboxNavCreatePayload) => void
  /** Save handler for rename / edit. */
  onUpdate?: (payload: MailboxNavUpdatePayload) => void
  /** Delete from overflow menu. */
  onDelete?: (sectionId: string, itemId: string) => void
  /** Panel width. Default `240`. */
  width?: number | string
  className?: string
  darkMode?: boolean
  /** Static visual state for the design-system showcase only. */
  previewState?: MailboxNavPreviewState
}
